<template>
  <fts-drawer v-if="visible" position="right" width="400px" @close="close">
    <!-- header with conditional back button -->
    <template #drawerHeader>
      <div v-if="showDetailView" class="listing-drawer-header">
        <div class="listing-drawer-header-content" style="justify-content: flex-start;">
          <fts-btn icon text @click="closeDetailView">
            <font-awesome-icon :icon="['fas', 'chevron-right']" style="transform: rotate(180deg);" />
          </fts-btn>
          <h4 class="listing-drawer-header-title">
            {{ carrierMode ? 'Page design' : (isEditingListing ? 'Edit view' : 'New view') }}
          </h4>
        </div>
        <hr>
      </div>
      <div v-else class="listing-drawer-header">
        <div class="listing-drawer-header-content">
          <h4 class="listing-drawer-header-title">
            Views
          </h4>
          <fts-btn icon text @click="close">
            <font-awesome-icon :icon="['fas', 'times']" />
          </fts-btn>
        </div>
        <hr>
      </div>
    </template>

    <!-- body -->
    <template #drawerContent>
      <!-- Detail view -->
      <div v-if="showDetailView" class="listing-detail-view">
        <div class="listing-detail-content">
          <!-- Listing form -->
          <div class="listing-form">
            <!-- Page design (the default view, R13): the module page everyone sees while it has no views -->
            <div class="listing-form-field listing-carrier-note" v-if="carrierMode">
              Everyone with access sees every entry on this page while the module has no views.
              Pick its columns here; the card comes from the Design control on the page.
            </div>
            <!-- Name field with emoji -->
            <div class="listing-form-field" v-else>
              <div class="listing-name-field">
                <fts-emoji :append-to-body="true" :toggle-size="24" v-model="listingFormData.emoji" />
                <input v-model="listingFormData.name" type="text" class="listing-name-input"
                  placeholder="Name this view" />
              </div>
              <!-- error message -->
              <div class="design-modal-form-error" v-if="error('name')">
                {{ error("name") }}
              </div>
            </div>

            <!-- List designer -->
            <div class="fts-form-group fts-form-group--comfort">
              <fts-list-pills-designer :informations="information" v-model="listingFormData.list" :createInformation="createInformation" />
              <!-- error message -->
              <div class="design-modal-form-error list-design-error" v-if="error('list')">
                {{ error("list") }} Click above and start to customize your list.
              </div>
            </div>

            <!-- Advanced filter (scope never enters the page design, R11) -->
            <div class="listing-form-field" v-if="!carrierMode">
              <div class="listing-form-toggle">
                <label class="listing-form-label">Advanced filter</label>
                <fts-toggle-switch v-model="listingFormData.allowAdvancedFilter" :label="false" :size="24" />
              </div>
              <div v-if="listingFormData.allowAdvancedFilter" class="fts-col fts-form-group fts-form-group--comfort">
                <fts-query-designer :modules="modules" :states="states"
                  :information-source="getDefaultFilterSelection()" :filter="listingFormData.filters" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- List view -->
      <div v-else class="listing-drawer-body">
        <div class="listing-drawer-content">
          <!-- Empty state -->
          <div v-if="!listings || listings.length === 0" class="listing-empty-state">
            <div class="listing-empty-state__icon">
              <img :src="emptyViewsImage" alt="empty-state-image">
            </div>

            <div class="listing-empty-state__title">No views created yet</div>

            <div class="listing-empty-state__description">
              Create views to organize and display your data.
            </div>

            <fts-btn 
              color="primary" 
              box-shadow-none 
              text-transform-init 
              @click="createListing"
            >
              <font-awesome-icon :icon="['fas', 'plus']" />
              New View
            </fts-btn>

            <fts-btn text text-transform-init color="var(--inistate-text-secondary-color)"
              class="listing-empty-state__design" @click="editListDesign">
              <font-awesome-icon :icon="['fas', 'pencil']" />
              Design this page
            </fts-btn>
          </div>

          <!-- Listing list -->
          <div v-else>
            <p style="margin-bottom: 20px;">All views of [{{ vectorName }}]</p>
            <draggable v-model="listingData.listing" v-bind="dragOptions" handle=".listing-item__handle"
              @start="onDrag = true" @end="onListingDragEnd">
              <div v-for="listing in listings" :key="listing.id" class="listing-item">
                <!-- handle -->
                <font-awesome-icon :icon="['fas', 'grip-vertical']" class="listing-item__handle" />
                <!-- content -->
                <div class="listing-item-content">
                  <fts-label-group :item="listing" emoji />
                </div>
                <!-- actions -->
                <div class="listing-item__actions">
                  <fts-btn icon text @click="editListing(listing.id)">
                    <font-awesome-icon :icon="['fas', 'pen']" />
                  </fts-btn>
                  <fts-btn icon text @click="deleteListing(listing)">
                    <font-awesome-icon :icon="['fas', 'trash']" />
                  </fts-btn>
                </div>
              </div>
            </draggable>

            <!-- Create listing button -->
            <fts-btn @click="createListing" text text-transform-init color="var(--inistate-text-secondary-color)"
              style="font-weight: var(--inistate-font-weight-light); justify-content: flex-start;">
              <font-awesome-icon :icon="['fas', 'plus']" />
              <p> New view</p>
            </fts-btn>
          </div>
        </div>
      </div>
    </template>

    <template #drawerLowerContent v-if="showDetailView && isEditingListing && !carrierMode">
      <div class="info-drawer-actions">
        <fts-btn text text-transform-init @click="remove">
          <font-awesome-icon :icon="['far', 'trash-alt']" /> Delete
        </fts-btn>
        <div class="info-drawer-actions-spacer"></div>
      </div>
    </template>
  </fts-drawer>
</template>

<script>
import draggable from "vuedraggable";
import changesets from 'diff-json';
import commons from "../commons/commons";
import FTSDrawer from "../elements/FTSDrawer";
import FTSBtn from "../elements/FTSBtn";
import FTSTippy from "../elements/FTSTippy";
import FTSLabelGroup from "../elements/FTSLabelGroup";
import FTSEmoji from "../elements/FTSEmoji";
import FTSToggleSwitch from "../elements/FTSToggleSwitch";
import FTSListPillsDesigner from "../design/FTSListPillsDesigner";
import FTSFormLabel from '../elements/FTSFormLabel';
import { store } from "../store/canvasDesignStore";
import { clientStore } from "../store/clientStore";
import queryLogic from '../commons/queryLogic';
import FTSQueryDesigner from './FTSQueryDesigner';
import canvasDesign from '../commons/canvasDesign';
import validator from "../commons/designValidator";
import { emptyViewsImage } from "../commons/images";

export default {
  store,
  components: {
    draggable,
    "fts-drawer": FTSDrawer,
    "fts-btn": FTSBtn,
    "fts-tippy": FTSTippy,
    "fts-label-group": FTSLabelGroup,
    "fts-emoji": FTSEmoji,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-list-pills-designer": FTSListPillsDesigner,
    "fts-form-label": FTSFormLabel,
    "fts-query-designer": FTSQueryDesigner,
  },
  props: {
    listingData: {
      type: Object,
      default: () => ({
        vectorName: '',
        listing: []
      })
    },
  },
  data: () => ({
    visible: false,
    onDrag: false,
    dragOptions: {
      animation: 200,
      disabled: false,
      ghostClass: 'ghost'
    },
    showDetailView: false,
    listingFormData: {
      name: '',
      emoji: '📋',
      allowAdvancedFilter: false,
      filters: {
        items: [],
      },
    },
    originalFormData: null,
    editingListingId: null,
    isEditingListing: false,
    /* Editing the page design (canvas.listDesign) rather than a view: no name, no filter, no delete. */
    carrierMode: false,
    vectorName: '',
    emptyViewsImage
  }),
  validations() {
    // The page design has no name or filter, and its columns may stay empty (derived until designed, R5).
    if (this.carrierMode) return { listingFormData: { name: {}, list: { columns: {} }, filter: {} } };
    const menusList = this.$store.getters.canvasMenus
    const listingFormData = validator.menuDesign(menusList);
    return { listingFormData };
  },
  watch: {
    'listingFormData.name'(newValue) {
      if(this.originalFormData && this.originalFormData.name === newValue){
        return;
      }

      if (newValue && newValue.trim()) { 
        this.debounceAutoEmojiInput();
      }
    }
  },
  computed: {
    listings() {
      return (this.listingData && this.listingData.listing) || [];
    },
    information() {

      const informations = this.$store.getters.canvasInformation;

      const hasStateColumn = this.listingFormData.list.columns.some(
        col => col.id === 'state'
      )

      const hasAnyState = this.states.length > 0;

      if(!hasStateColumn && hasAnyState) {
        return  [ ...informations, {
          id: 'state',
          displayName: 'State',
          system: true,
          fieldName: 'state',
          type: 'state',
        }];
      }

      return informations;
    },
    modules() {
      return this.$store.getters.workspaceModules;
    },
    states() {
      return this.$store.getters.canvasStates;
    },
    userCollection() {
      return (
        this.$store.state.vector && this.$store.state.vector.isUserCollection
      );
    },
  },
  methods: {
    debounceAutoEmojiInput: _.debounce(async function () {
      if (this.listingFormData.name && this.listingFormData.name.trim()) {
        try {
          const res = await this.$store.dispatch("ai", {
            type: "emoji",
            text: this.listingFormData.name.trim()
          });
          if (!this.listingFormData.id) return;
          this.listingFormData.emoji = res.data;
          console.log("Autofill view emoji:", res.data);
        } catch (err) {
          console.error("AI emoji generation error:", err);
        }
      }
    }, 800),
    updateDefaultSelection() {
      // This method is called when list designer updates
      // Can be used for additional logic when selection changes
    },
    show() {
      this.visible = true;
      this.vectorName = this.listingData.vectorName || '';
    },
    async close() {
      let dismiss = true;
      if(this.showDetailView) dismiss = await this.closeDetailView();

      if(!dismiss) return;
      this.visible = false; 
    },
    error(key) {
      return validator.menuDesignError(this.$v.listingFormData, key);
    },
    async closeDetailView() {

      return new Promise(async (resolve) => {
        this.$v.listingFormData.$touch();
        if (!this.$v.listingFormData.$invalid) {
          this.saveListing();
          this.showDetailView = false;
          this.resetListingForm();
          resolve(true);
          return;
        } else if( !this.$v.listingFormData.name.required && !this.$v.listingFormData.list.columns.required && !this.isEditingListing){
          this.showDetailView = false;
          this.resetListingForm();
          resolve(true);
          return;
        }

        this.$modal.show("dialog", {
          title: "Confirm discard changes",
          text: "Discard all changes?",
          buttons: [
            { title: "Cancel", handler: () => {
              this.$modal.hide("dialog"); 
              resolve(false);
            } },
            {
              title: "Discard",
              default: true,
              handler: () => {
                this.$modal.hide("dialog");
                this.showDetailView = false;
                this.resetListingForm();
                resolve(true);
              },
            },
          ],
        });

        console.warn("ListingDrawer:InvalidForm", validator.translateValidations(this.$v.listingFormData, true));
        return;
      });

    },
    resetListingForm() {
      this.$v.listingFormData.$reset();
      this.listingFormData = {
        name: '',
        emoji: '',
        allowAdvancedFilter: false,
        filters: {
          items: [],
        },
        list: {
          columns: [],
        }
      };
      this.originalFormData = null;
      this.editingListingId = null;
      this.isEditingListing = false;
      this.carrierMode = false;
    },
    /* The page design (R13, §5.7): the module page's own columns, edited exactly like a view minus
       scope. Lives on canvas.listDesign, never in menus[]. */
    editListDesign() {
      const design = this.$store.getters.canvasListDesign || {};
      this.resetListingForm();
      this.carrierMode = true;
      this.listingFormData = {
        id: 'default',
        name: 'Default',
        emoji: '',
        allowAdvancedFilter: false,
        filters: { items: [] },
        list: _.cloneDeep(design.list && Array.isArray(design.list.columns) ? design.list : { columns: [] })
      };
      this.showDetailView = true;
      this.$nextTick(() => {
        this.originalFormData = _.cloneDeep(this.listingFormData);
      });
    },
    async saveListDesign() {
      try {
        let canvas = _.cloneDeep(this.$store.state.canvas);
        const design = canvasDesign.listDesignOf(canvas);
        design.list = { columns: this.listingFormData.list.columns };
        this.$set(canvas, 'listDesign', design);

        commons.compactClassification(canvas);
        this.$store.dispatch("updateVectorCanvas", canvas);
        await this.$store.dispatch("saveVector");

        window.eventBus.$emit("listing:schemaUpdated");
        this.$toasted.success("Page design saved.");

        this.originalFormData = _.cloneDeep(this.listingFormData);
        this.showDetailView = false;
        this.resetListingForm();
      } catch (err) {
        console.error(err);
        const message = err && err.response && err.response.data && err.response.data.message;
        this.$toasted.error(`Failed to save the page design. ${message || 'Please try again.'}`);
      }
    },
    createListing() {
      this.showDetailView = true;
      this.resetListingForm();
      this.listingFormData.id = commons.newUUID();

      // Store original data for change detection
      this.$nextTick(() => {
        this.originalFormData = _.cloneDeep(this.listingFormData);
      });
    },
    editListing(listingId) {
      const listing = this.listings.find(item => item.id === listingId);
      // Load listing data into form
      this.listingFormData = {
        id: listing.id,
        name: listing.name || '',
        emoji: listing.emoji || '',
        default: listing.default || false,
        allowAdvancedFilter: (listing.filter && listing.filter.items && listing.filter.items.length > 0) || false,
        filters: listing.filter || { items: [] },
        list: listing.list || { columns: [] }
      };

      // Set editing flags
      this.editingListingId = listing.id;
      this.isEditingListing = true;

      // Show detail view
      this.showDetailView = true;

      // Store original data for change detection
      this.$nextTick(() => {
        this.originalFormData = _.cloneDeep(this.listingFormData);
      });
    },
    async saveListing() {
      if(this.originalFormData){
        const diff = changesets.diff(this.listingFormData, this.originalFormData);
          if (!diff || !diff.length) {
          return;
        }
      }

      if (this.carrierMode) return this.saveListDesign();

      try {
        // Prepare listing object (same structure for both create and update)
        const result = {
          id:  this.listingFormData.id,
          name: this.listingFormData.name.trim(),
          emoji: this.listingFormData.emoji,
          informationType: this.listingFormData.informationType,
          filter: this.listingFormData.filters,
          list: this.listingFormData.list,
          type: 'listing'
        };

        // Initialize missing fields (like FTSListingDesignModal.show() does)
        /* default filter */
        if (!result.defaultFilter) {
          result.defaultFilter = { items: [] };
        }
        /* default app filter */
        if (!result.defaultAppFilter) {
          result.defaultAppFilter = { items: [] };
        }
        /* mobile list design */
        if (!result.mobileListDesign) {
          result.mobileListDesign = { columns: [] };
        }
        /* profile only */
        if (result.profileOnly === undefined) {
          result.profileOnly = null;
        }

        // Clone canvas from store
        let canvas = _.cloneDeep(this.$store.state.canvas);

        // Get or initialize menus array
        let items = canvas.menus;
        if (!items) {
          items = [];
        }

        // Find if listing exists
        const findIndex = items.findIndex(x => x.id === result.id);
        const isUpdate = findIndex > -1;

        if (isUpdate) {
          // UPDATE: Replace existing item
          this.$set(items, findIndex, result);
        } else {
          // CREATE: Add new item
          items.push(result);
        }

        // Remove default listing if exists
        if (items.length > 1) {
          const defaultListingIndex = items.findIndex(
            item => item.id === "defaultListing" && item.default === true
          );
          if (defaultListingIndex > -1) {
            items.splice(defaultListingIndex, 1);
          }
        }

        // Update canvas
        this.$set(canvas, 'menus', items);

        // Compact classification
        commons.compactClassification(canvas);

        // Update store
        this.$store.dispatch("updateVectorCanvas", canvas);

        // Save to backend
        await this.$store.dispatch("saveVector");

        // Reload workspace to sync clientStore with updated menus (for sidebar)
        await clientStore.dispatch("loadWorkspace", {
          id: clientStore.getters.workspaceId,
          forceReload: true,
          preserveMenuState: true
        });

        // Notify views to refresh their schema
        window.eventBus.$emit("listing:schemaUpdated");

        // Show success notification
        this.$toasted.success(
          isUpdate
            ? `View "${result.name}" has been updated.`
            : `View "${result.name}" has been created.`
        );

        // Update original data after successful save to prevent confirmation dialog
        this.originalFormData = _.cloneDeep(this.listingFormData);

        // Close detail view and reset form
        this.showDetailView = false;
        this.resetListingForm();
      } catch (err) {
        console.error(err);
        this.$toasted.error(`Failed to save view. ${err.response.data.message || 'Please try again.'}`);
      }
    },
    async remove() {
      const listing = this.listings.find(item => item.id === this.editingListingId);
      this.deleteListing(listing);
    },
    async deleteListing(listing) {
      this.$modal.show("dialog", {
        title: "Confirm delete",
        text: `Are you sure you want to delete "${listing.name}"? This action cannot be undone.`,
        buttons: [
          {
            title: "Cancel",
            handler: () => {
              this.$modal.hide("dialog");
              throw Error("User cancelled delete");
            },
          },
          {
            title: "Delete",
            default: true,
            handler: async () => {
              try {
                this.$modal.hide("dialog");

                // Clone canvas
                let canvas = _.cloneDeep(this.$store.state.canvas);

                // 找到当前 listing index
                const listingIndex = canvas.menus.findIndex(
                  l => l.id === listing.id
                );

                // 删除
                if (listingIndex !== -1) {
                  canvas.menus.splice(listingIndex, 1);
                }

                // Update store
                await this.$store.dispatch("updateVectorCanvas", canvas);

                await this.$store.dispatch("saveVector");
                await clientStore.dispatch("loadWorkspace", {
                  id: clientStore.getters.workspaceId,
                  forceReload: true,
                  preserveMenuState: true
                });

                window.eventBus.$emit("listing:schemaUpdated");

                this.$toasted.success(`${listing.name} has been deleted.`);

                this.resetListingForm();
                this.showDetailView = false;

                // ✅ 关键逻辑：取前一个 (a real view — never the legacy placeholder row)
                let targetListing = null;

                if (listingIndex > 0) {
                  targetListing = canvas.menus.slice(0, listingIndex).reverse()
                    .find(x => !canvasDesign.isLegacyDefaultListing(x)) || null;
                }

                // fallback
                const targetId = targetListing?.id || "everything";

                console.log("Redirect target:", targetId);

                // 当前是 studio
                if (this.$route.name === "lite-studio") {
                  window.eventBus.$emit("lite:switchListView", targetId, listing.id);
                } else {
                  // 非 studio 用 router
                  const moduleId = clientStore.getters.moduleId;

                  this.$router.replace({
                    name: "lite",
                    params: { moduleId },
                    query: { l: targetId }
                  });
                }

              } catch (err) {
                throw err;
              }
            },
          },
        ],
      });
    },
    async onListingDragEnd() {
      // Set drag flag to false
      this.onDrag = false;

      // Clone canvas from store
      let canvas = _.cloneDeep(this.$store.state.canvas);

      // Update the menus with the reordered array. The list shows real views only; the legacy
      // placeholder row stays in the canvas until the Phase 2 app is the floor (R9).
      const legacyRows = (canvas.menus || []).filter(canvasDesign.isLegacyDefaultListing);
      canvas.menus = [...this.listingData.listing, ...legacyRows];

      // Update store
      this.$store.dispatch("updateVectorCanvas", canvas);

      // Save to backend
      await this.$store.dispatch("saveVector");

      await clientStore.dispatch("loadWorkspace", {
        id: clientStore.getters.workspaceId,
        forceReload: true,
        preserveMenuState: true
      });
      
      window.eventBus.$emit("listing:schemaUpdated");

    },
    filterSelection(type) {
      let differences = ["date"];

      if (type === 'app') {
        differences = differences.concat(["lastChangeBy", "lastChangeDate"]);
      }
      return _.differenceWith(
        queryLogic.querySelectionPrefix,
        this.userCollection ? [...differences] : [...differences, "username"],
        ({ id }, key) => id === key
      )
        .concat(
          this.information.filter(x => {
            const informationType = commons.informationTypes.find(y => y.id == x.type);
            return informationType && informationType.searchable;
          })
        )
        .concat(queryLogic.querySelectionSuffix);
    },
    getDefaultFilterSelection(type) {
      const columns = this.listingFormData.list && this.listingFormData.list.columns;
      return this.filterSelection(type);
    },

    /* list columns needed */
    async createInformation(item) {
      let previousInformations = store.getters.canvasInformation;
      let canvas = _.cloneDeep(store.state.canvas);
      let res = canvasDesign.initInformation(item);

      let increaseIndex = 0;
      if (item.displayName) {
        while (true) {
          const displayName = res.displayName + (increaseIndex > 0 ? ` ${increaseIndex}` : '');

          const duplicatedIndex = previousInformations.findIndex(
            x => x.displayName === displayName
          );

          if (duplicatedIndex === -1) {
            // Unique found → set the name and exit
            res.displayName = displayName;
            break;
          }

          increaseIndex++;
        }
      }

      /* create informations to main canvas */
      try {
        canvasDesign.setInformation(canvas, res);
        await store.dispatch("updateVectorCanvas", canvas);
      } catch (error) {
        console.error(error);
        throw new Error(error);
      }

      return res;
    },
    async designExecuteSave(payload) {
      try {
        console.info("designExecuteSave:Starting", payload);
        const res = await store.dispatch("saveVector", payload);
        console.info("designExecuteSave:Completed", res);

        // Reload workspace to sync sidebar with updated module info
        await clientStore.dispatch("loadWorkspace", {
          id: clientStore.getters.workspaceId,
          forceReload: true,
          preserveMenuState: true
        });

        // Notify views to refresh their schema
        window.eventBus.$emit("listing:schemaUpdated");
      } catch (err) {
        console.error('Auto-save failed:', err);
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.listing-drawer-header {
  padding-top: 1rem;

  hr {
    margin: 0;
    border: 0;
    border-top: 1px solid var(--fts-border);
  }
}

.listing-drawer-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
}

.listing-drawer-header-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--fts-text);
}

.listing-drawer-body {
  height: 100%;
  width: 100%;
  overflow: auto;
}

.listing-drawer-content {
  padding: 1.5rem;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.listing-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 320px;
  padding: 3rem 0;
  margin: 0 auto;
  text-align: center;
  flex: 1;

  &__icon {
    max-width: 100%;
    width: 160px;
    margin-bottom: 12px;
    > img {
      display: flex;
      align-items: center;
      justify-content: center;
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 100%;
      margin: auto;
    }
  }

  &__title {
    margin-bottom: 6px;
    font-weight: var(--inistate-font-weight-medium);
    font-size: var(--inistate-paragraph-text-16);
    line-height: var(--inistate-paragraph-text-16--line-height);
    color: var(--inistate-text-primary);
  }

  &__description {
    margin-bottom: 16px;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-label-text-14--line-height);
    color: var(--inistate-text-secondary);
  }
}

.listing-item {
  position: relative;
  display: flex;
  align-items: center;
  padding: 1rem 0.375rem 1rem 1.375rem;
  margin-bottom: 0.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: rgba(var(--fts-gray-100-rgb), 0.75);

  &:hover {
    background-color: rgba(var(--fts-gray-100-rgb), 0.75);

    .listing-item__handle,
    .listing-item__actions {
      visibility: visible !important;
    }
  }

  &__handle {
    position: absolute;
    left: 0.375rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--fts-default);
    font-size: 0.75rem;
    visibility: hidden;
    cursor: grab;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-left: auto;
    visibility: hidden;
  }

  &-content {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex: 1;
  }
}

.ghost {
  opacity: 0.5;
  background: var(--fts-background-secondary);
}

.listing-detail-view {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.listing-detail-content {
  flex: 1;
  padding: 1.5rem;
  overflow: auto;
}

.listing-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  hr {
    margin: 0;
    border: 0;
    border-top: 1px solid var(--fts-border);
  }
}

.listing-form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.fts-form-group {
  margin-bottom: 1.5rem;

  &.fts-form-group--comfort {
    margin-bottom: 1.5rem;
  }
}

.design-modal-form-error {
  margin-top: 0.5rem;
  padding: 0.75rem;
  font-size: 0.875rem;
  color: var(--fts-danger);
  background-color: rgba(var(--fts-danger-rgb), 0.1);
  border-radius: 0.25rem;
  border-left: 3px solid var(--fts-danger);

  &.list-design-error {
    margin-top: 0.75rem;
  }
}

.listing-designer-toggle-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background-color: var(--fts-background);
  border: 1px solid var(--fts-border);
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(var(--fts-gray-100-rgb), 0.5);
    border-color: var(--fts-primary);
  }

  span {
    font-size: 0.875rem;
    color: var(--fts-text);
  }

  svg {
    color: var(--fts-text-secondary);
    font-size: 0.875rem;
  }
}

.listing-form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--fts-text);
}

.listing-form-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.listing-name-field {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--fts-border);
  border-radius: 0.25rem;
  background-color: var(--fts-background);
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: var(--fts-primary);
  }
}

.listing-name-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.875rem;
  color: var(--fts-text);
  background: transparent;

  &::placeholder {
    color: var(--fts-text-secondary);
  }
}

.listing-select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.listing-select {
  flex: 1;
  padding: 0.5rem 2rem 0.5rem 0.5rem;
  font-size: 0.875rem;
  color: var(--fts-text);
  background-color: var(--fts-background);
  border: 1px solid var(--fts-border);
  border-radius: 0.25rem;
  outline: none;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: var(--fts-primary);
  }

  option {
    color: var(--fts-text);
    background-color: var(--fts-background);
  }
}

.listing-select-icon {
  position: absolute;
  right: 0.75rem;
  pointer-events: none;
  color: var(--fts-text-secondary);
  font-size: 0.75rem;
}

.listing-form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
}

.listing-inline-input {
  flex: 1;
  padding: 0.5rem;
  font-size: 0.875rem;
  color: var(--fts-text);
  background-color: var(--fts-background);
  border: 1px solid var(--fts-border);
  border-radius: 0.25rem;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: var(--fts-primary);
  }

  &::placeholder {
    color: var(--fts-text-secondary);
  }
}

// Footer
.info-drawer-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--fts-border);
}

.info-drawer-actions-spacer {
  flex: 1;
}
</style>
